import { BaseApiClient } from './base-client'
import { API_ENDPOINTS } from './config'

export interface Order {
  id: string
  symbol: string
  side: 'buy' | 'sell'
  type: string
  quantity: number
  price?: number
  status: string
  created_at: string
  /** Present when QTP tagged the order to an agent. Empty/omitted = untagged. */
  agent_id?: string
  /** Selected customer connection that owns this OMS order. */
  connection_id?: string
}
export interface Position {
  symbol: string
  quantity: number
  avgPrice: number
  side?: string
  agent_id?: string
  connection_id?: string
}

export interface FlattenAgentResponse {
  ok: boolean
  agent_id: string
  orders_placed: number
  positions: number
  orders?: { order_id: string; symbol: string; status: string }[]
  errors?: string[]
  message?: string
}
export interface Portfolio {
  value: number;
  cash: number;
  positions: Position[];
  // Backend sometimes returns snake_case fields; keep optional for compatibility
  total_value?: number;
  invested_amount?: number;
  unrealized_pnl?: number;
  realized_pnl?: number;
  cash_balance?: number;
}
export interface Analytics { sharpe: number; volatility: number; beta: number }
/**
 * Create-order body for QTP `models.OrderRequest` (JSON tags):
 *   symbol, side, quantity, order_type, price?, limit_price?, stop_price?
 * Portal sends time_in_force (day|gtc|ioc|fok). QTP validates per order type
 * and forwards it to the broker. Empty TIF defaults to day, not GTC.
 * Do not send Alpaca-style `qty` / `type` alone — QTP will fail validation.
 */
export interface CreateOrderRequest {
  symbol: string;
  side: 'buy' | 'sell';
  /** QTP: order_type ("market" | "limit" | "stop" | "stop_limit") */
  order_type: string;
  /** QTP: quantity */
  quantity: number;
  price?: number;
  limit_price?: number;
  stop_price?: number;
  time_in_force?: string;
  client_order_id?: string;
  /** Selected customer connection for manual/agent execution. Chat paper confirm omits it. */
  connection_id?: string;
  /** QTP execution environment. Manual orders mirror the selected connection. */
  environment?: 'paper' | 'live';
  agent_id?: string;
  /** @deprecated use order_type — kept optional for transitional callers */
  type?: string;
  /** @deprecated use quantity — kept optional for transitional callers */
  qty?: number;
}

export class TradingApiClient extends BaseApiClient {
  async getAccount() { return this.get(API_ENDPOINTS.trading.account) }
  async getOrders(filters?: { connectionId?: string }) {
    const params = new URLSearchParams()
    if (filters?.connectionId) params.set('connection_id', filters.connectionId)
    const query = params.toString()
    return this.get<Order[]>(
      `${API_ENDPOINTS.trading.orders}${query ? `?${query}` : ''}`
    )
  }
  async getOrder(orderId: string) { return this.get<Order>(`${API_ENDPOINTS.trading.orders}/${orderId}`) }
  async createOrder(orderData: CreateOrderRequest) { return this.post<Order>(API_ENDPOINTS.trading.orders, orderData) }
  async cancelOrder(orderId: string) { return this.delete(`${API_ENDPOINTS.trading.orders}/${orderId}`) }
  async updateOrder(orderId: string, updates: Partial<CreateOrderRequest>) { return this.patch<Order>(`${API_ENDPOINTS.trading.orders}/${orderId}`, updates) }
  async getPositions() { return this.get<Position[]>(API_ENDPOINTS.trading.positions) }
  async getAgentPositions(agentId: string, connectionId: string) {
    const query = new URLSearchParams({ connection_id: connectionId })
    return this.get<Position[]>(
      `/api/v1/trading/agents/${encodeURIComponent(agentId)}/positions?${query}`
    )
  }
  async flattenAgent(agentId: string, connectionId?: string) {
    return this.post<FlattenAgentResponse>(
      `/api/v1/trading/agents/${encodeURIComponent(agentId)}/flatten`,
      connectionId ? { connection_id: connectionId } : {}
    )
  }
  async getPosition(symbol: string) { return this.get<Position>(`${API_ENDPOINTS.trading.positions}/${symbol}`) }
  async getPortfolio() { return this.get<Portfolio>(API_ENDPOINTS.trading.portfolio) }
  async getAnalytics() { return this.get<Analytics>(API_ENDPOINTS.trading.analytics) }
  async getPerformance() { return this.get(API_ENDPOINTS.trading.performance) }
  async getRebalancingRecommendations() { return this.get(API_ENDPOINTS.trading.rebalancing) }
  async executeRebalancing(data: any) { return this.post(API_ENDPOINTS.trading.rebalancing, data) }

  // Additional methods expected by hooks
  async placeOrder(orderData: CreateOrderRequest) { return this.createOrder(orderData) }
  async modifyOrder(orderId: string, updates: Partial<CreateOrderRequest>) { return this.updateOrder(orderId, updates) }
  async getOrderHistory(filters?: { symbol?: string; startDate?: string; endDate?: string }) {
    const params = new URLSearchParams()
    if (filters?.symbol) params.append('symbol', filters.symbol)
    if (filters?.startDate) params.append('start_date', filters.startDate)
    if (filters?.endDate) params.append('end_date', filters.endDate)
    const query = params.toString()
    return this.get<Order[]>(`${API_ENDPOINTS.trading.orders}/history${query ? `?${query}` : ''}`)
  }
}

